// src\components\AiDashboard\AiMyNutritionStatus\AiUser\AiUserSettings\nutritionTargetsSettings.ts
import {
  ACTIVITY_LEVEL_MAP,
  physicalActivityOptions,
  advancedActivityTypeOptions,
  UI_COPY,
} from "./AiUserSettingConst";

// NNR2023 dataset (CommonJS module exported from nnr2023-data.js)
import * as nnr2023Data from "../../nnr2023-data";

/**
 * Shape of the Firestore user profile document we care about.
 * We keep an index signature so additional fields are not lost.
 */
export interface FirestoreUserProfileLike {
  // Canonical profile fields
  birthDateIso?: string;
  hasAcknowledgedBirthDate?: boolean;
  sex?: string;

  // Mirrors / alternates that may exist in Firestore
  dateOfBirth?: string;
  confirmDob?: boolean;
  Sex?: string;

  // Height variants
  heightCentimeters?: number;
  heightCm?: number;
  heightFt?: number;
  heightInch?: number;

  // Weight variants
  weightKilograms?: number;
  weightKg?: number;
  weightLb?: number;

  // Activity
  physicalActivityLevelId?: string;
  activityLevel?: number;

  // Advanced activity
  advancedActivityTypeId?: string;
  advancedActivityMinutesPerDay?: number;
  AdvancedActivityLevelType?: string;
  AdvancedActivityLevel?: number;

  // Weight loss goal (new)
  weightLoss?: boolean;
  looseWeight?: number;

  // Gating / trial
  profileSetUpComplete?: boolean;
  isTrialPeriodActive?: boolean;
  accountCreatedSource?: string;
  accountCreatedAt?: unknown;
  startTrialPeriodDate?: unknown;
  endTrialPeriodDate?: unknown;

  [key: string]: unknown;
}

/**
 * Shape of a single dailyIntake document in users/{uid}/dailyIntake/{yyyy-MM-dd}.
 * We keep it loose but document the core fields we expect.
 */
export interface DailyIntakeDocumentLike {
  dateIso?: string;
  totals?: { [nutrientKey: string]: number };
  byMealType?: {
    [mealType: string]: { [nutrientKey: string]: number };
  };
  macros?: { [macroName: string]: unknown };
  antioxidants?: { [field: string]: unknown } | null;
  dailyValueReferenceSnapshot?: { [nutrientKey: string]: number };
  updatedAt?: unknown;
  [key: string]: unknown;
}

/**
 * Shape of the antioxidants object on a single meal document.
 * This mirrors the structure you already store on each meal:
 *  - label
 *  - level
 *  - perNutrientPct
 *  - score0to100
 *  - consolidated_nutrients
 */
export interface MealAntioxidantsLike {
  label?: string;
  level?: string;
  perNutrientPct?: Record<string, number>;
  score0to100?: number;
  consolidated_nutrients?: Record<string, number>;
  [key: string]: unknown;
}

/**
 * Minimal shape of a meal document for antioxidants aggregation.
 * This is intentionally small: only the antioxidants block is required here.
 */
export interface MealDocumentWithAntioxidantsLike {
  antioxidants?: MealAntioxidantsLike | null;
  [key: string]: unknown;
}

/**
 * Daily antioxidants summary stored on users/{uid}/dailyIntake/{dateIso}.antioxidants.
 * This matches the examples you posted:
 *
 *   antioxidants: {
 *     label: "Antioxidants: Low (28%)",
 *     level: "low",
 *     perNutrientPct: { ... },
 *     score0to100: 28,
 *     consolidated_nutrients: { ... }
 *   }
 */
export interface DailyAntioxidantsSummary {
  label: string;
  level: "low" | "moderate" | "high";
  perNutrientPct: Record<string, number>;
  score0to100: number;
  consolidated_nutrients: Record<string, number>;
}

/**
 * Options for building the nutrition targets settings JSON snapshot.
 */
export interface NutritionTargetsSettingsBuilderOptions {
  userProfile: FirestoreUserProfileLike | null;
  /**
   * Map from yyyy-MM-dd → dailyIntake document for that date.
   * These are the already-aggregated daily snapshots from Firestore.
   */
  dailyIntakeByDate?: Record<string, DailyIntakeDocumentLike> | null;
}

/**
 * Shape of a single NNR life stage age range.
 */
interface NnrLifeStageAgeRange {
  min?: number | null;
  max?: number | null;
  note?: string;
}

/**
 * Minimal shape of a NNR life stage row.
 * We keep this intentionally loose to stay aligned with nnr2023-data.js.
 */
interface NnrLifeStageLike {
  id?: string;
  label?: string;
  age_years?: NnrLifeStageAgeRange;
  condition?: string;
  [key: string]: unknown;
}

/**
 * Minimal shape of the NNR2023 dataset module.
 */
interface Nnr2023ModuleLike {
  meta?: {
    version?: string;
    source?: string;
    [key: string]: unknown;
  };
  lifeStages?: NnrLifeStageLike[];
  [key: string]: unknown;
}

/**
 * Snapshot of the NNR life-stage reference we matched for this profile.
 * This is what we render in the "UseDaily JSON" pane.
 */
export interface NnrReferenceSnapshot {
  source: string;
  version: string;
  generatedAtIso: string;
  profile: {
    ageYears: number | null;
    sex: string | null;
  };
  lifeStageId: string | null;
  lifeStageLabel: string | null;
  /**
   * The matched life stage row from nnr2023-data.js, pruned to the core fields.
   * This is intentionally generic to follow the dataset as it evolves.
   */
  lifeStage: Record<string, unknown> | null;
}

/**
 * JSON snapshot that the dev JSON modal will render for the "UserFirebase JSON" pane.
 * This is strictly the data coming from Firestore (user profile + dailyIntake).
 */
export interface NutritionTargetsSettingsSnapshot {
  source: "nutritionTargetsSettings";
  version: string;
  generatedAtIso: string;

  isProfileSetUpComplete: boolean;

  profile: {
    birthDateIso: string | null;
    hasAcknowledgedBirthDate: boolean;
    ageYears: number | null;
    sex: string | null;
  };

  body: {
    height: {
      centimeters: number | null;
      meters: number | null;
    };
    weight: {
      kilograms: number | null;
    };
    bodyMassIndex: {
      value: number | null;
      classification: string | null;
    };
  };

  weightLossGoal: {
    isActive: boolean;
    kilogramsToLose: number | null;
    minRecommendedKg: number;
    maxRecommendedKg: number;
  };

  activity: {
    physicalActivityLevelId: string | null;
    physicalActivityLabel: string | null;
    activityLevelIndex: number | null;
    advancedTraining: {
      advancedActivityTypeId: string | null;
      advancedActivityLabel: string | null;
      advancedActivityMinutesPerDay: number | null;
    };
  };

  /**
   * Daily intake snapshots for the currently selected period, keyed by yyyy-MM-dd.
   * These come directly from users/{uid}/dailyIntake/{dateIso}.
   */
  dailyIntakeByDate: Record<string, DailyIntakeDocumentLike> | null;

  /**
   * Pruned Firestore profile snapshot for debugging.
   * Only keeps a minimal, non-duplicated subset.
   */
  rawProfile: FirestoreUserProfileLike | null;
}

/**
 * Helper for numeric coercion from unknown input.
 */
function coerceNumberOrNull(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Calculate age in years from an ISO date string (yyyy-MM-dd).
 */
function calculateAgeInYears(
  birthDateIso: string | undefined | null,
): number | null {
  if (!birthDateIso || typeof birthDateIso !== "string") {
    return null;
  }

  const birthDate = new Date(birthDateIso);
  if (Number.isNaN(birthDate.getTime())) {
    return null;
  }

  const now = new Date();
  let ageYears = now.getFullYear() - birthDate.getFullYear();

  const hasNotHadBirthdayThisYear =
    now.getMonth() < birthDate.getMonth() ||
    (now.getMonth() === birthDate.getMonth() &&
      now.getDate() < birthDate.getDate());

  if (hasNotHadBirthdayThisYear) {
    ageYears -= 1;
  }

  return ageYears >= 0 ? ageYears : null;
}

/**
 * Calculate body mass index and a simple classification.
 */
function calculateBodyMassIndex(
  heightCentimeters: number | null,
  weightKilograms: number | null,
): { value: number | null; classification: string | null } {
  if (
    heightCentimeters === null ||
    heightCentimeters <= 0 ||
    weightKilograms === null ||
    weightKilograms <= 0
  ) {
    return { value: null, classification: null };
  }

  const heightMeters = heightCentimeters / 100;
  if (heightMeters <= 0) {
    return { value: null, classification: null };
  }

  const rawIndex = weightKilograms / (heightMeters * heightMeters);
  const bodyMassIndexValue = Number.isFinite(rawIndex)
    ? Number(rawIndex.toFixed(1))
    : null;

  if (bodyMassIndexValue === null) {
    return { value: null, classification: null };
  }

  let classification: string;

  if (bodyMassIndexValue < 18.5) {
    classification = "Underweight";
  } else if (bodyMassIndexValue < 25) {
    classification = "Normal weight";
  } else if (bodyMassIndexValue < 30) {
    classification = "Overweight";
  } else {
    classification = "Obesity";
  }

  return { value: bodyMassIndexValue, classification };
}

/**
 * Resolve the canonical height in centimeters from the profile.
 * Prefers heightCentimeters, falls back to heightCm, otherwise null.
 */
function resolveHeightCentimeters(
  profile: FirestoreUserProfileLike | null,
): number | null {
  if (!profile) {
    return null;
  }

  const valueFromCanonical = coerceNumberOrNull(
    profile.heightCentimeters,
  );
  if (valueFromCanonical !== null) {
    return valueFromCanonical;
  }

  const valueFromMirror = coerceNumberOrNull(profile.heightCm);
  if (valueFromMirror !== null) {
    return valueFromMirror;
  }

  return null;
}

/**
 * Resolve the canonical weight in kilograms from the profile.
 * Prefers weightKilograms, falls back to weightKg, otherwise null.
 */
function resolveWeightKilograms(
  profile: FirestoreUserProfileLike | null,
): number | null {
  if (!profile) {
    return null;
  }

  const valueFromCanonical = coerceNumberOrNull(
    profile.weightKilograms,
  );
  if (valueFromCanonical !== null) {
    return valueFromCanonical;
  }

  const valueFromMirror = coerceNumberOrNull(profile.weightKg);
  if (valueFromMirror !== null) {
    return valueFromMirror;
  }

  return null;
}

/**
 * Resolve baseline physical activity from profile.
 */
function resolvePhysicalActivityInfo(
  profile: FirestoreUserProfileLike | null,
): {
  physicalActivityLevelId: string | null;
  physicalActivityLabel: string | null;
  activityLevelIndex: number | null;
} {
  if (!profile) {
    return {
      physicalActivityLevelId: null,
      physicalActivityLabel: null,
      activityLevelIndex: null,
    };
  }

  const physicalActivityLevelIdRaw =
    typeof profile.physicalActivityLevelId === "string" &&
    profile.physicalActivityLevelId.trim() !== ""
      ? profile.physicalActivityLevelId.trim()
      : null;

  const physicalActivityOption = physicalActivityLevelIdRaw
    ? physicalActivityOptions.find(
        (option) => option.id === physicalActivityLevelIdRaw,
      )
    : undefined;

  let activityLevelIndexRaw: number | null = null;
  if (physicalActivityLevelIdRaw) {
    const typedKey =
      physicalActivityLevelIdRaw as keyof typeof ACTIVITY_LEVEL_MAP;
    if (typedKey in ACTIVITY_LEVEL_MAP) {
      activityLevelIndexRaw = ACTIVITY_LEVEL_MAP[typedKey];
    }
  }
  if (activityLevelIndexRaw === null) {
    activityLevelIndexRaw = coerceNumberOrNull(profile.activityLevel);
  }

  return {
    physicalActivityLevelId: physicalActivityLevelIdRaw,
    physicalActivityLabel: physicalActivityOption
      ? physicalActivityOption.label
      : null,
    activityLevelIndex: activityLevelIndexRaw,
  };
}

/**
 * Resolve advanced training information from profile.
 */
function resolveAdvancedTrainingInfo(
  profile: FirestoreUserProfileLike | null,
): {
  advancedActivityTypeId: string | null;
  advancedActivityLabel: string | null;
  advancedActivityMinutesPerDay: number | null;
} {
  if (!profile) {
    return {
      advancedActivityTypeId: null,
      advancedActivityLabel: null,
      advancedActivityMinutesPerDay: null,
    };
  }

  const typeFromCanonical =
    typeof profile.advancedActivityTypeId === "string" &&
    profile.advancedActivityTypeId.trim() !== ""
      ? profile.advancedActivityTypeId.trim()
      : null;

  const typeFromMirror =
    typeof profile.AdvancedActivityLevelType === "string" &&
    profile.AdvancedActivityLevelType.trim() !== ""
      ? profile.AdvancedActivityLevelType.trim()
      : null;

  const advancedActivityTypeId =
    typeFromCanonical || typeFromMirror || null;

  const minutesFromCanonical = coerceNumberOrNull(
    profile.advancedActivityMinutesPerDay,
  );
  const minutesFromMirror = coerceNumberOrNull(
    profile.AdvancedActivityLevel,
  );
  const advancedActivityMinutesPerDay =
    minutesFromCanonical !== null
      ? minutesFromCanonical
      : minutesFromMirror !== null
      ? minutesFromMirror
      : null;

  const advancedActivityOption = advancedActivityTypeId
    ? advancedActivityTypeOptions.find(
        (option) => option.id === advancedActivityTypeId,
      )
    : undefined;

  return {
    advancedActivityTypeId,
    advancedActivityLabel: advancedActivityOption
      ? advancedActivityOption.label
      : null,
    advancedActivityMinutesPerDay,
  };
}

/**
 * Build a pruned rawProfile object:
 *  - removes duplicate / mirror fields;
 *  - removes accountCreatedSource, endTrialPeriodDate, weightKilograms;
 *  - keeps only a minimal, useful subset.
 */
function buildRawProfileForSnapshot(
  profile: FirestoreUserProfileLike | null,
): FirestoreUserProfileLike | null {
  if (!profile || typeof profile !== "object") {
    return null;
  }

  const pruned: FirestoreUserProfileLike = {};

  // Gate flag
  if (typeof profile.profileSetUpComplete === "boolean") {
    pruned.profileSetUpComplete = profile.profileSetUpComplete;
  }

  // Trial flag (keep simple boolean; drop endTrialPeriodDate)
  if (typeof profile.isTrialPeriodActive === "boolean") {
    pruned.isTrialPeriodActive = profile.isTrialPeriodActive;
  }

  // Important timestamps (for debugging, but not noisy)
  if (profile.accountCreatedAt) {
    pruned.accountCreatedAt = profile.accountCreatedAt;
  }
  if (profile.startTrialPeriodDate) {
    pruned.startTrialPeriodDate = profile.startTrialPeriodDate;
  }
  // NOTE: deliberately NOT including accountCreatedSource or endTrialPeriodDate.

  // Core profile inputs
  if (typeof profile.birthDateIso === "string") {
    pruned.birthDateIso = profile.birthDateIso;
  } else if (typeof profile.dateOfBirth === "string") {
    // Normalise legacy dateOfBirth into birthDateIso in raw view
    pruned.birthDateIso = profile.dateOfBirth;
  }

  if (typeof profile.hasAcknowledgedBirthDate === "boolean") {
    pruned.hasAcknowledgedBirthDate =
      profile.hasAcknowledgedBirthDate;
  }

  if (typeof profile.sex === "string") {
    pruned.sex = profile.sex;
  } else if (typeof profile.Sex === "string") {
    pruned.sex = profile.Sex;
  }

  // Height: keep a single canonical numeric + the ft/in view if present
  const heightCentimeters =
    typeof profile.heightCentimeters === "number"
      ? profile.heightCentimeters
      : typeof profile.heightCm === "number"
      ? profile.heightCm
      : undefined;

  if (typeof heightCentimeters === "number") {
    pruned.heightCentimeters = heightCentimeters;
  }

  if (typeof profile.heightFt === "number") {
    pruned.heightFt = profile.heightFt;
  }
  if (typeof profile.heightInch === "number") {
    pruned.heightInch = profile.heightInch;
  }

  // Weight: keep kg and lb integers; drop weightKilograms to avoid duplication
  if (typeof profile.weightKg === "number") {
    pruned.weightKg = profile.weightKg;
  }
  if (typeof profile.weightLb === "number") {
    pruned.weightLb = profile.weightLb;
  }

  // Weight-loss plan
  if (typeof profile.weightLoss === "boolean") {
    pruned.weightLoss = profile.weightLoss;
  }
  const looseWeightNumber = coerceNumberOrNull(profile.looseWeight);
  if (looseWeightNumber !== null) {
    pruned.looseWeight = looseWeightNumber;
  }

  // Activity
  if (typeof profile.physicalActivityLevelId === "string") {
    pruned.physicalActivityLevelId =
      profile.physicalActivityLevelId;
  }
  if (typeof profile.activityLevel === "number") {
    pruned.activityLevel = profile.activityLevel;
  }

  // Advanced activity (canonical + mirrors)
  if (typeof profile.advancedActivityTypeId === "string") {
    pruned.advancedActivityTypeId =
      profile.advancedActivityTypeId;
  }
  if (
    typeof profile.advancedActivityMinutesPerDay === "number"
  ) {
    pruned.advancedActivityMinutesPerDay =
      profile.advancedActivityMinutesPerDay;
  }
  if (typeof profile.AdvancedActivityLevelType === "string") {
    pruned.AdvancedActivityLevelType =
      profile.AdvancedActivityLevelType;
  }
  if (typeof profile.AdvancedActivityLevel === "number") {
    pruned.AdvancedActivityLevel = profile.AdvancedActivityLevel;
  }

  return pruned;
}

/**
 * Check whether a NNR life stage has a numeric age range and that it contains the given age.
 */
function nnrLifeStageMatchesAge(
  ageYears: number,
  ageRange: NnrLifeStageAgeRange | undefined,
): boolean {
  if (!ageRange) {
    return false;
  }

  const min =
    typeof ageRange.min === "number" && Number.isFinite(ageRange.min)
      ? ageRange.min
      : 0;
  const maxRaw =
    typeof ageRange.max === "number" && Number.isFinite(ageRange.max)
      ? ageRange.max
      : null;
  const max =
    maxRaw === null ? Number.POSITIVE_INFINITY : maxRaw;

  return ageYears >= min && ageYears <= max;
}

/**
 * Resolve the NNR life stage row for a given age and sex.
 * Sex-specific rows (male_*, female_*) are preferred; otherwise we fall back to neutral rows.
 * When multiple rows match, we pick the one with the highest minimum age (most specific).
 */
function resolveNnrLifeStageForAgeAndSex(
  nnrModule: Nnr2023ModuleLike,
  ageYears: number | null,
  sex: string | null,
): NnrLifeStageLike | null {
  if (ageYears === null || !Number.isFinite(ageYears)) {
    return null;
  }

  const lifeStages = Array.isArray(nnrModule.lifeStages)
    ? nnrModule.lifeStages
    : [];

  if (lifeStages.length === 0) {
    return null;
  }

  const normalizedSex =
    typeof sex === "string" && sex.trim() !== ""
      ? sex.trim().toLowerCase()
      : null;

  // First filter by age range.
  const ageMatches: NnrLifeStageLike[] =
    lifeStages.filter((lifeStage) => {
      if (!lifeStage || typeof lifeStage !== "object") {
        return false;
      }

      const range = lifeStage.age_years;
      if (!range) {
        return false;
      }

      return nnrLifeStageMatchesAge(ageYears, range);
    });

  if (ageMatches.length === 0) {
    return null;
  }

  // Prefer sex-specific rows if available (male_*, female_*)
  let candidates: NnrLifeStageLike[] = ageMatches;

  if (normalizedSex === "male" || normalizedSex === "female") {
    const prefix = `${normalizedSex}_`;
    const sexSpecific = ageMatches.filter((lifeStage) => {
      const id =
        typeof lifeStage.id === "string"
          ? lifeStage.id.toLowerCase()
          : "";
      return id.startsWith(prefix);
    });

    if (sexSpecific.length > 0) {
      candidates = sexSpecific;
    }
  }

  // Choose the row with the highest min age (most specific for this age).
  const sortedByMinAgeDescending = [...candidates].sort(
    (left, right) => {
      const leftRange = left.age_years || {};
      const rightRange = right.age_years || {};

      const leftMin =
        typeof leftRange.min === "number" &&
        Number.isFinite(leftRange.min)
          ? leftRange.min
          : 0;
      const rightMin =
        typeof rightRange.min === "number" &&
        Number.isFinite(rightRange.min)
          ? rightRange.min
          : 0;

      return rightMin - leftMin;
    },
  );

  return sortedByMinAgeDescending[0] || null;
}

/**
 * Build the NNR reference snapshot for the given profile characteristics.
 * This snapshot is what we show in the "UseDaily JSON" pane (middle column).
 */
function buildNnrReferenceSnapshot(
  ageYears: number | null,
  sex: string | null,
): NnrReferenceSnapshot | null {
  const nnrModule: Nnr2023ModuleLike =
    nnr2023Data as unknown as Nnr2023ModuleLike;

  const source = nnrModule.meta?.source || "NNR2023";
  const version = nnrModule.meta?.version || "unknown";

  const matchedLifeStage = resolveNnrLifeStageForAgeAndSex(
    nnrModule,
    ageYears,
    sex,
  );

  if (!matchedLifeStage) {
    // We still return a minimal object so downstream consumers can see why it is empty.
    return {
      source,
      version,
      generatedAtIso: new Date().toISOString(),
      profile: {
        ageYears,
        sex: sex ?? null,
      },
      lifeStageId: null,
      lifeStageLabel: null,
      lifeStage: null,
    };
  }

  const lifeStageId =
    typeof matchedLifeStage.id === "string"
      ? matchedLifeStage.id
      : null;
  const lifeStageLabel =
    typeof matchedLifeStage.label === "string"
      ? matchedLifeStage.label
      : null;

  // Build a pruned copy of the life stage so the JSON is stable and readable.
  const lifeStageAgeYears: NnrLifeStageAgeRange | undefined =
    matchedLifeStage.age_years;
  const lifeStageMacros = (matchedLifeStage as any).macros;
  const lifeStageBmi = (matchedLifeStage as any).bmi;
  const lifeStageEnergy = (matchedLifeStage as any).energy;
  const lifeStageProteinByKg =
    (matchedLifeStage as any).proteinByKg;
  const lifeStageNutrients = (matchedLifeStage as any).nutrients;
  const lifeStageCondition = matchedLifeStage.condition;

  const lifeStageSnapshot: Record<string, unknown> = {
    id: lifeStageId,
    label: lifeStageLabel,
    ...(lifeStageAgeYears ? { age_years: lifeStageAgeYears } : {}),
    ...(lifeStageMacros ? { macros: lifeStageMacros } : {}),
    ...(lifeStageBmi ? { bmi: lifeStageBmi } : {}),
    ...(lifeStageEnergy ? { energy: lifeStageEnergy } : {}),
    ...(lifeStageProteinByKg
      ? { proteinByKg: lifeStageProteinByKg }
      : {}),
    ...(lifeStageNutrients
      ? { nutrients: lifeStageNutrients }
      : {}),
    ...(lifeStageCondition
      ? { condition: lifeStageCondition }
      : {}),
  };

  return {
    source,
    version,
    generatedAtIso: new Date().toISOString(),
    profile: {
      ageYears,
      sex: sex ?? null,
    },
    lifeStageId,
    lifeStageLabel,
    lifeStage: lifeStageSnapshot,
  };
}

/**
 * Small helper class that builds the JSON snapshot used by the MyNutritionStatus dev modal.
 * This snapshot is strictly Firestore-based (UserFirebase view).
 */
export class NutritionTargetsSettingsBuilder {
  public static buildFromUserProfile(
    options: NutritionTargetsSettingsBuilderOptions,
  ): NutritionTargetsSettingsSnapshot {
    const { userProfile, dailyIntakeByDate } = options;

    const normalizedProfile: FirestoreUserProfileLike | null =
      userProfile && typeof userProfile === "object"
        ? userProfile
        : null;

    const birthDateIso =
      normalizedProfile &&
      typeof normalizedProfile.birthDateIso === "string"
        ? normalizedProfile.birthDateIso
        : null;

    const hasAcknowledgedBirthDate =
      normalizedProfile &&
      typeof normalizedProfile.hasAcknowledgedBirthDate ===
        "boolean"
        ? normalizedProfile.hasAcknowledgedBirthDate
        : false;

    const sexValue =
      normalizedProfile &&
      typeof normalizedProfile.sex === "string"
        ? normalizedProfile.sex
        : null;

    const heightCentimeters =
      resolveHeightCentimeters(normalizedProfile);
    const weightKilograms =
      resolveWeightKilograms(normalizedProfile);
    const ageYears = calculateAgeInYears(birthDateIso);
    const bodyMassIndex = calculateBodyMassIndex(
      heightCentimeters,
      weightKilograms,
    );

    const physicalActivityInfo =
      resolvePhysicalActivityInfo(normalizedProfile);
    const advancedTrainingInfo =
      resolveAdvancedTrainingInfo(normalizedProfile);

    const isProfileSetUpComplete = Boolean(
      normalizedProfile && normalizedProfile.profileSetUpComplete,
    );

    const rawProfilePruned = buildRawProfileForSnapshot(
      normalizedProfile,
    );

    // Weight-loss goal snapshot using canonical Firestore fields
    const weightLossFlag =
      normalizedProfile &&
      typeof normalizedProfile.weightLoss === "boolean"
        ? normalizedProfile.weightLoss
        : false;

    const looseWeightValue = normalizedProfile
      ? coerceNumberOrNull(normalizedProfile.looseWeight)
      : null;

    const weightLossGoalConfig: any =
      (UI_COPY as any)?.weightLoss?.goal || {};
    const minRecommendedKg =
      typeof weightLossGoalConfig.minRecommendedKg ===
      "number"
        ? weightLossGoalConfig.minRecommendedKg
        : 2;
    const maxRecommendedKg =
      typeof weightLossGoalConfig.maxRecommendedKg ===
      "number"
        ? weightLossGoalConfig.maxRecommendedKg
        : 50;

    // Note: NNR reference snapshot is built separately and used only for UseDaily JSON (middle pane).
    return {
      source: "nutritionTargetsSettings",
      version: "2024-01-01",
      generatedAtIso: new Date().toISOString(),

      isProfileSetUpComplete,

      profile: {
        birthDateIso,
        hasAcknowledgedBirthDate,
        ageYears,
        sex: sexValue,
      },

      body: {
        height: {
          centimeters: heightCentimeters,
          meters:
            heightCentimeters !== null
              ? heightCentimeters / 100
              : null,
        },
        weight: {
          kilograms: weightKilograms,
        },
        bodyMassIndex,
      },

      weightLossGoal: {
        isActive: weightLossFlag,
        kilogramsToLose: looseWeightValue,
        minRecommendedKg,
        maxRecommendedKg,
      },

      activity: {
        physicalActivityLevelId:
          physicalActivityInfo.physicalActivityLevelId,
        physicalActivityLabel:
          physicalActivityInfo.physicalActivityLabel,
        activityLevelIndex:
          physicalActivityInfo.activityLevelIndex,
        advancedTraining: {
          advancedActivityTypeId:
            advancedTrainingInfo.advancedActivityTypeId,
          advancedActivityLabel:
            advancedTrainingInfo.advancedActivityLabel,
          advancedActivityMinutesPerDay:
            advancedTrainingInfo.advancedActivityMinutesPerDay,
        },
      },

      dailyIntakeByDate: dailyIntakeByDate ?? null,

      rawProfile: rawProfilePruned,
    };
  }
}

/**
 * Convenience function so MyNutritionStatus can stay declarative:
 * it simply calls this builder with the already-loaded snapshot.
 */
export function buildNutritionTargetsSettingsPreview(
  options: NutritionTargetsSettingsBuilderOptions,
): NutritionTargetsSettingsSnapshot {
  return NutritionTargetsSettingsBuilder.buildFromUserProfile(
    options,
  );
}

/**
 * Format the settings snapshot as pretty JSON string for display in dev modal.
 */
export function formatNutritionTargetsSettingsAsJSON(
  snapshot: NutritionTargetsSettingsSnapshot,
): string {
  return JSON.stringify(snapshot, null, 2);
}

/**
 * Build the UseDaily preview JSON from NNR2023 data.
 * This is the middle pane in the dev JSON modal showing the matched life stage reference.
 */
export function buildUseDailyPreviewFromNnr2023(
  options: NutritionTargetsSettingsBuilderOptions,
): NnrReferenceSnapshot | null {
  const { userProfile } = options;

  if (!userProfile) {
    return null;
  }

  const birthDateIso =
    typeof userProfile.birthDateIso === "string"
      ? userProfile.birthDateIso
      : null;

  const sex =
    typeof userProfile.sex === "string" ? userProfile.sex : null;

  const ageYears = calculateAgeInYears(birthDateIso);

  return buildNnrReferenceSnapshot(ageYears, sex);
}

/**
 * Build a single daily antioxidants summary from all meal-level antioxidants objects.
 *
 * Rules:
 *  - If no meal has antioxidants → return null (dailyIntake.antioxidants will be null).
 *  - Level = "worst" level across meals (low < moderate < high).
 *  - score0to100 = average of meal score0to100 (rounded).
 *  - perNutrientPct = average of meal perNutrientPct for each nutrient (rounded).
 *  - consolidated_nutrients = sum of meal consolidated_nutrients values per nutrient.
 *
 * This function is intended to be called by the code that compiles and writes
 * users/{uid}/dailyIntake/{dateIso}. It does not perform any Firestore I/O itself.
 */
export function buildDailyAntioxidantsFromMeals(
  mealDocuments: MealDocumentWithAntioxidantsLike[],
): DailyAntioxidantsSummary | null {
  if (!Array.isArray(mealDocuments) || mealDocuments.length === 0) {
    return null;
  }

  const mealAntioxidantsList: MealAntioxidantsLike[] =
    mealDocuments
      .map(
        (mealDocument) => mealDocument.antioxidants,
      )
      .filter(
        (antioxidants): antioxidants is MealAntioxidantsLike =>
          Boolean(antioxidants),
      );

  if (mealAntioxidantsList.length === 0) {
    return null;
  }

  const perNutrientTotals: Record<
    string,
    { sum: number; count: number }
  > = {};
  const consolidatedNutrientsTotals: Record<string, number> = {};

  let totalScore = 0;
  let scoreCount = 0;

  const levelPriority: Record<string, number> = {
    low: 0,
    moderate: 1,
    high: 2,
  };

  let worstLevel: "low" | "moderate" | "high" = "high";

  for (const antioxidants of mealAntioxidantsList) {
    // Normalise level and pick the worst (lowest priority)
    const rawLevel =
      typeof antioxidants.level === "string"
        ? antioxidants.level.trim().toLowerCase()
        : "low";

    const normalizedLevel: "low" | "moderate" | "high" =
      rawLevel === "moderate" || rawLevel === "high"
        ? (rawLevel as "moderate" | "high")
        : "low";

    if (levelPriority[normalizedLevel] < levelPriority[worstLevel]) {
      worstLevel = normalizedLevel;
    }

    // Aggregate score
    if (
      typeof antioxidants.score0to100 === "number" &&
      Number.isFinite(antioxidants.score0to100)
    ) {
      totalScore += antioxidants.score0to100;
      scoreCount += 1;
    }

    // Aggregate perNutrientPct (average later)
    if (
      antioxidants.perNutrientPct &&
      typeof antioxidants.perNutrientPct === "object"
    ) {
      for (const [nutrientKey, value] of Object.entries(
        antioxidants.perNutrientPct,
      )) {
        if (
          typeof value !== "number" ||
          !Number.isFinite(value)
        ) {
          continue;
        }

        if (!perNutrientTotals[nutrientKey]) {
          perNutrientTotals[nutrientKey] = {
            sum: 0,
            count: 0,
          };
        }

        perNutrientTotals[nutrientKey].sum += value;
        perNutrientTotals[nutrientKey].count += 1;
      }
    }

    // Aggregate consolidated_nutrients (sum)
    if (
      antioxidants.consolidated_nutrients &&
      typeof antioxidants.consolidated_nutrients ===
        "object"
    ) {
      for (const [nutrientKey, value] of Object.entries(
        antioxidants.consolidated_nutrients,
      )) {
        if (
          typeof value !== "number" ||
          !Number.isFinite(value)
        ) {
          continue;
        }

        const previousValue =
          consolidatedNutrientsTotals[nutrientKey] ?? 0;
        consolidatedNutrientsTotals[nutrientKey] =
          previousValue + value;
      }
    }
  }

  if (scoreCount === 0) {
    // If there is no numeric score, return null so the caller can decide what to do.
    return null;
  }

  const score0to100 = Math.round(totalScore / scoreCount);

  const perNutrientPct: Record<string, number> = {};
  for (const [nutrientKey, { sum, count }] of Object.entries(
    perNutrientTotals,
  )) {
    if (count > 0) {
      perNutrientPct[nutrientKey] = Math.round(sum / count);
    }
  }

  const levelLabel =
    worstLevel.charAt(0).toUpperCase() +
    worstLevel.slice(1).toLowerCase();

  const label = `Antioxidants: ${levelLabel} (${score0to100}%)`;

  return {
    label,
    level: worstLevel,
    perNutrientPct,
    score0to100,
    consolidated_nutrients: consolidatedNutrientsTotals,
  };
}
